// lib/timeline-video-types.ts
import crypto from 'crypto';

type TimelineRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type TimelineTrackLayerType = 'video' | 'image' | 'audio';

export type TimelineTrackInput = {
  layerId: string;
  layerType?: TimelineTrackLayerType | null;
  sourceSrc?: string | null;
  videoSrc?: string | null;
  imageSrc?: string | null;
  audioEnabled?: boolean | null;
  audioGain?: number | null;
  renderOrder: number;
  startMs: number;
  endMs: number | null;
  sourceInMs: number;
  playbackRate?: number | null;
  fadeInRatio?: number | null;
  fadeOutRatio?: number | null;
  rotationDeg?: number | null;
  flipX?: boolean | null;
  flipY?: boolean | null;
  opacity?: number | null;
  rect: TimelineRect;
  scale: number;
};

export type TimelineExportPayload = {
  workspaceId?: string;
  output?: {
    width?: number;
    height?: number;
    fps?: number;
    format?: 'mp4' | 'mov';
    quality?: 'draft' | 'balanced' | 'high';
    requireMp4?: boolean;
    includeAudio?: boolean;
    profile?: 'export' | 'workspace-preview' | 'reference';
  };
  range?: {
    startMs?: number | null;
    endMs?: number | null;
  };
  tracks?: TimelineTrackInput[];
};

export type TimelineComposeUserContext = {
  dbUserId: string;
  authId: string;
  isFaterAdmin: boolean;
  userOrgId: string | null;
};

type TimelineCompositionPhase =
  | 'queued'
  | 'pulling'
  | 'building'
  | 'finalizing'
  | 'ready'
  | 'error'
  | 'missing';

export type TimelineCompositionStatus = {
  cacheKey: string;
  phase: TimelineCompositionPhase;
  status: 'processing' | 'ready' | 'error' | 'missing';
  percent: number;
  tracksPulled: number;
  totalTracks: number;
  message: string;
  error?: string;
  updatedAt: number;
};

export type TimelineExportStatusSocketSource =
  | 'worker'
  | 'inline';

export type TimelineExportStatusSocketPayload = {
  cacheKey: string;
  workspaceId: string | null;
  status: TimelineCompositionStatus;
  cached?: boolean;
  source: TimelineExportStatusSocketSource;
  emittedAt: string;
};

export const TIMELINE_COMPOSITION_TTL_MS = 2 * 60 * 60 * 1000;
export const TIMELINE_EXPORT_MAX_DIMENSION = 4096;
export const TIMELINE_EXPORT_MAX_DURATION_MS = 60 * 60 * 1000;
const TIMELINE_COMPOSITION_CACHE_VERSION = 'timeline-compose-v11-output-space-track-rects';
const MIN_TIMELINE_TRACK_PLAYBACK_RATE = 0.25;
const MAX_TIMELINE_TRACK_PLAYBACK_RATE = 4;

export const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export const toFinite = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const normalizeVideoDimension = (value: number): number => {
  const rounded = Math.max(2, Math.round(value));
  return rounded % 2 === 0 ? rounded : rounded - 1;
};

export const normalizeTimelineTrackPlaybackRate = (value: unknown): number => {
  const parsed = toFinite(value);
  if (parsed === null) return 1;
  return Math.round(clamp(parsed, MIN_TIMELINE_TRACK_PLAYBACK_RATE, MAX_TIMELINE_TRACK_PLAYBACK_RATE) * 1000) / 1000;
};

export const resolveTrackLayerType = (track: TimelineTrackInput): TimelineTrackLayerType => {
  if (track.layerType === 'image') return 'image';
  if (track.layerType === 'audio') return 'audio';
  return 'video';
};

export const resolveTrackSource = (track: TimelineTrackInput): string => {
  const candidates = [track.sourceSrc, track.videoSrc, track.imageSrc];
  const firstValid = candidates.find(
    (candidate) => typeof candidate === 'string' && candidate.trim().length > 0
  );
  return typeof firstValid === 'string' ? firstValid.trim() : '';
};

export const buildTimelineCacheKey = (
  payload: TimelineExportPayload,
  scopeKey: string
): string => {
  const tracks = (Array.isArray(payload.tracks) ? payload.tracks : [])
    .map((track) => ({
      layerId: String(track.layerId || ''),
      layerType: resolveTrackLayerType(track),
      sourceSrc: resolveTrackSource(track),
      audioEnabled: track.audioEnabled !== false,
      audioGain: clamp(toFinite(track.audioGain) ?? 1, 0, 8),
      renderOrder: Number.isFinite(track.renderOrder) ? Math.round(track.renderOrder) : 0,
      startMs: Math.max(0, Math.round(toFinite(track.startMs) ?? 0)),
      endMs: toFinite(track.endMs),
      sourceInMs: Math.max(0, Math.round(toFinite(track.sourceInMs) ?? 0)),
      playbackRate: normalizeTimelineTrackPlaybackRate(track.playbackRate),
      fadeInRatio: clamp(toFinite(track.fadeInRatio) ?? 0, 0, 1),
      fadeOutRatio: clamp(toFinite(track.fadeOutRatio) ?? 0, 0, 1),
      rotationDeg: toFinite(track.rotationDeg) ?? 0,
      flipX: track.flipX === true,
      flipY: track.flipY === true,
      opacity: clamp(toFinite(track.opacity) ?? 1, 0, 1),
      rect: {
        x: Number.isFinite(track.rect?.x) ? Number(track.rect.x) : 0,
        y: Number.isFinite(track.rect?.y) ? Number(track.rect.y) : 0,
        w: Number.isFinite(track.rect?.w) ? Number(track.rect.w) : 0,
        h: Number.isFinite(track.rect?.h) ? Number(track.rect.h) : 0,
      },
      scale: Number.isFinite(track.scale) ? Number(track.scale) : 1,
    }))
    .sort((a, b) => {
      if (a.renderOrder !== b.renderOrder) return a.renderOrder - b.renderOrder;
      if (a.layerId !== b.layerId) return a.layerId.localeCompare(b.layerId);
      if (a.layerType !== b.layerType) return a.layerType.localeCompare(b.layerType);
      return a.sourceSrc.localeCompare(b.sourceSrc);
    });

  const normalized = {
    version: TIMELINE_COMPOSITION_CACHE_VERSION,
    scopeKey,
    workspaceId: typeof payload.workspaceId === 'string' ? payload.workspaceId.trim() : '',
    output: {
      width: normalizeVideoDimension(toFinite(payload.output?.width) ?? 1280),
      height: normalizeVideoDimension(toFinite(payload.output?.height) ?? 720),
      fps: clamp(Math.round(toFinite(payload.output?.fps) ?? 24), 8, 60),
      format: payload.output?.format === 'mov' ? 'mov' : 'mp4',
      quality:
        payload.output?.quality === 'draft'
          ? 'draft'
          : (payload.output?.quality === 'high' ? 'high' : 'balanced'),
      requireMp4: payload.output?.requireMp4 === true,
      includeAudio: payload.output?.includeAudio !== false,
      profile:
        payload.output?.profile === 'workspace-preview'
          ? 'workspace-preview'
          : (payload.output?.profile === 'reference' ? 'reference' : 'export'),
    },
    range: {
      startMs: toFinite(payload.range?.startMs),
      endMs: toFinite(payload.range?.endMs),
    },
    tracks,
  };

  return crypto.createHash('sha256').update(JSON.stringify(normalized)).digest('hex');
};

const resolveTimelineOutputProfile = (payload: TimelineExportPayload): 'export' | 'workspace-preview' | 'reference' => {
  if (payload.output?.profile === 'workspace-preview') return 'workspace-preview';
  if (payload.output?.profile === 'reference') return 'reference';
  return 'export';
};

export const normalizeTimelineExportPayloadForComposition = (
  payload: TimelineExportPayload
): TimelineExportPayload => {
  if (resolveTimelineOutputProfile(payload) === 'workspace-preview') {
    return payload;
  }

  const tracks = Array.isArray(payload.tracks) ? payload.tracks : [];
  if (tracks.length === 0) return payload;

  const outputWidth = normalizeVideoDimension(toFinite(payload.output?.width) ?? 1280);
  const outputHeight = normalizeVideoDimension(toFinite(payload.output?.height) ?? 720);
  const visualBounds = tracks.reduce<{
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    indexes: Set<number>;
  }>((bounds, track, index) => {
    const layerType = resolveTrackLayerType(track);
    if (layerType === 'audio') return bounds;
    const rectX = toFinite(track.rect?.x) ?? 0;
    const rectY = toFinite(track.rect?.y) ?? 0;
    const rectW = toFinite(track.rect?.w) ?? 0;
    const rectH = toFinite(track.rect?.h) ?? 0;
    if (rectW <= 0 || rectH <= 0) return bounds;
    bounds.minX = Math.min(bounds.minX, rectX);
    bounds.minY = Math.min(bounds.minY, rectY);
    bounds.maxX = Math.max(bounds.maxX, rectX + rectW);
    bounds.maxY = Math.max(bounds.maxY, rectY + rectH);
    bounds.indexes.add(index);
    return bounds;
  }, {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
    indexes: new Set<number>(),
  });

  const boundsWidth = visualBounds.maxX - visualBounds.minX;
  const boundsHeight = visualBounds.maxY - visualBounds.minY;
  if (
    visualBounds.indexes.size === 0 ||
    !Number.isFinite(boundsWidth) ||
    !Number.isFinite(boundsHeight) ||
    boundsWidth <= 0 ||
    boundsHeight <= 0
  ) {
    return {
      ...payload,
      output: {
        ...payload.output,
        width: outputWidth,
        height: outputHeight,
      },
    };
  }

  const scaleX = outputWidth / boundsWidth;
  const scaleY = outputHeight / boundsHeight;
  return {
    ...payload,
    output: {
      ...payload.output,
      width: outputWidth,
      height: outputHeight,
    },
    tracks: tracks.map((track, index) => {
      if (!visualBounds.indexes.has(index)) return track;
      const rectX = toFinite(track.rect?.x) ?? 0;
      const rectY = toFinite(track.rect?.y) ?? 0;
      const rectW = toFinite(track.rect?.w) ?? 0;
      const rectH = toFinite(track.rect?.h) ?? 0;
      return {
        ...track,
        rect: {
          x: (rectX - visualBounds.minX) * scaleX,
          y: (rectY - visualBounds.minY) * scaleY,
          w: Math.max(2, rectW * scaleX),
          h: Math.max(2, rectH * scaleY),
        },
        scale: 1,
      };
    }),
  };
};

export const validateTimelineExportPayloadLimits = (
  payload: TimelineExportPayload
): string | null => {
  const widthRaw = toFinite(payload.output?.width);
  const heightRaw = toFinite(payload.output?.height);
  const width = widthRaw !== null ? normalizeVideoDimension(widthRaw) : null;
  const height = heightRaw !== null ? normalizeVideoDimension(heightRaw) : null;

  if (width !== null && width > TIMELINE_EXPORT_MAX_DIMENSION) {
    return `Video export width exceeds limit (${TIMELINE_EXPORT_MAX_DIMENSION}px max).`;
  }
  if (height !== null && height > TIMELINE_EXPORT_MAX_DIMENSION) {
    return `Video export height exceeds limit (${TIMELINE_EXPORT_MAX_DIMENSION}px max).`;
  }

  const startMs = toFinite(payload.range?.startMs);
  const endMs = toFinite(payload.range?.endMs);
  if (startMs !== null && endMs !== null) {
    const spanMs = Math.max(0, Math.round(endMs) - Math.round(startMs));
    if (spanMs > TIMELINE_EXPORT_MAX_DURATION_MS) {
      return `Video export range exceeds limit (${Math.round(TIMELINE_EXPORT_MAX_DURATION_MS / 60000)} minutes max).`;
    }
  }

  return null;
};

export const buildMissingTimelineStatus = (cacheKey: string): TimelineCompositionStatus => ({
  cacheKey,
  phase: 'missing',
  status: 'missing',
  percent: 0,
  tracksPulled: 0,
  totalTracks: 0,
  message: 'Not found',
  updatedAt: Date.now(),
});

export const mergeTimelineStatus = (
  cacheKey: string,
  previous: TimelineCompositionStatus | null,
  patch: Partial<TimelineCompositionStatus>
): TimelineCompositionStatus => {
  const hasErrorPatch = Object.prototype.hasOwnProperty.call(patch, 'error');
  return {
    cacheKey,
    phase: patch.phase ?? previous?.phase ?? 'queued',
    status: patch.status ?? previous?.status ?? 'processing',
    percent: clamp(Math.round(patch.percent ?? previous?.percent ?? 0), 0, 100),
    tracksPulled: Math.max(0, Math.round(patch.tracksPulled ?? previous?.tracksPulled ?? 0)),
    totalTracks: Math.max(0, Math.round(patch.totalTracks ?? previous?.totalTracks ?? 0)),
    message: patch.message ?? previous?.message ?? 'Queued',
    error: hasErrorPatch ? patch.error : previous?.error,
    updatedAt: Date.now(),
  };
};
